import React, { useState, useEffect } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldHalved, faKey, faQrcode, faCopy, faCheck, faTimes,
  faLock, faUnlock, faExclamationTriangle, faCircleInfo, faMobileAlt,
} from '@fortawesome/free-solid-svg-icons';
import { UiPageHeader, UiModal, UiButton } from '../../components/ui';

// ─── OTP input shared style ───────────────────────────────────────────────────

const OTP_INPUT_CLASS =
  'w-full text-center text-3xl font-mono tracking-[0.5em] px-4 py-4 border-2 ' +
  'border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 ' +
  'rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-500 ' +
  'focus:border-gray-400 dark:focus:border-slate-500 placeholder:tracking-[0.5em]';

// ─── Step badge ───────────────────────────────────────────────────────────────

const StepBadge: React.FC<{ n: number }> = ({ n }) => (
  <span
    className="w-6 h-6 rounded-full text-white text-xs flex items-center justify-center flex-shrink-0"
    style={{ background: 'var(--theme-primary-gradient)' }}
  >
    {n}
  </span>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const Admin2FAPage: React.FC = () => {
  const [enabled,   setEnabled]   = useState(false);
  const [loading,   setLoading]   = useState(true);
  const [setupMode, setSetupMode] = useState(false);

  const [qrCode,       setQrCode]       = useState('');
  const [secret,       setSecret]       = useState('');
  const [backupCodes,  setBackupCodes]  = useState<string[]>([]);
  const [otp,          setOtp]          = useState('');
  const [enablingOtp,  setEnablingOtp]  = useState(false);

  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disableOtp,       setDisableOtp]       = useState('');
  const [disabling,        setDisabling]        = useState(false);

  useEffect(() => { checkStatus(); }, []);

  const checkStatus = async () => {
    setLoading(true);
    try {
      const res = await merchantApi.get('/auth/admin/me');
      setEnabled(res.data.data?.twofa_enabled || false);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const handleSetup = async () => {
    try {
      const res = await merchantApi.post('/admin/employees/me/2fa/setup', {});
      const { qr_code, secret: secretKey, backup_codes } = res.data.data;
      setQrCode(qr_code);
      setSecret(secretKey);
      setBackupCodes(backup_codes || []);
      setSetupMode(true);
      toast.success('กรุณาสแกน QR Code ด้วย Google Authenticator');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาดในการตั้งค่า 2FA');
    }
  };

  const handleEnable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) { toast.error('กรุณากรอกรหัส OTP 6 หลัก'); return; }
    setEnablingOtp(true);
    try {
      await merchantApi.post('/admin/employees/me/2fa/enable', { otp });
      toast.success('เปิดใช้งาน 2FA สำเร็จ!');
      setEnabled(true);
      setSetupMode(false);
      setOtp('');
      const u = JSON.parse(localStorage.getItem('admin_user') || '{}');
      u.twofa_enabled = true;
      localStorage.setItem('admin_user', JSON.stringify(u));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'รหัส OTP ไม่ถูกต้อง');
    } finally {
      setEnablingOtp(false);
    }
  };

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!disableOtp || disableOtp.length !== 6) { toast.error('กรุณากรอกรหัส OTP 6 หลัก'); return; }
    setDisabling(true);
    try {
      await merchantApi.post('/admin/employees/me/2fa/disable', { otp: disableOtp });
      toast.success('ปิดใช้งาน 2FA สำเร็จ!');
      setEnabled(false);
      setShowDisableModal(false);
      setDisableOtp('');
      const u = JSON.parse(localStorage.getItem('admin_user') || '{}');
      u.twofa_enabled = false;
      localStorage.setItem('admin_user', JSON.stringify(u));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'รหัส OTP ไม่ถูกต้อง');
    } finally {
      setDisabling(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('คัดลอกแล้ว!');
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-full mx-auto">

      <UiPageHeader
        icon={setupMode ? faQrcode : faShieldHalved}
        title={setupMode ? 'ตั้งค่า Two-Factor Authentication' : 'Two-Factor Authentication'}
        subtitle={setupMode ? 'สแกน QR Code ด้วยแอป Google Authenticator' : 'เพิ่มความปลอดภัยให้บัญชีของคุณ'}
      />

      {/* ── Setup Mode ─────────────────────────────────────────────────── */}
      {setupMode ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme overflow-hidden">

          {/* Step 1: QR Code */}
          <div className="p-6 border-b border-gray-100 dark:border-slate-700">
            <h3 className="font-semibold text-gray-800 dark:text-slate-100 mb-4 flex items-center gap-2">
              <StepBadge n={1} /> สแกน QR Code
            </h3>
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="p-4 bg-white rounded-xl border-2 border-gray-100 dark:border-slate-600 shadow-inner flex-shrink-0">
                <img src={qrCode} alt="QR Code" className="w-48 h-48" />
              </div>
              <div className="flex-1 w-full">
                <p className="text-sm text-gray-600 dark:text-slate-400 mb-3">หรือกรอก Secret Key ด้วยตัวเอง:</p>
                <div className="flex items-center gap-2 bg-gray-50 dark:bg-slate-700 rounded-xl p-3">
                  <code className="flex-1 text-sm font-mono break-all text-gray-700 dark:text-slate-200">{secret}</code>
                  <button
                    onClick={() => copyToClipboard(secret)}
                    className="w-10 h-10 rounded-lg bg-white dark:bg-slate-600 shadow-sm hover:shadow-md flex items-center justify-center text-gray-500 dark:text-slate-300 hover:text-gray-700 dark:hover:text-slate-100 transition-all flex-shrink-0"
                  >
                    <FontAwesomeIcon icon={faCopy} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Step 2: Backup Codes */}
          <div className="p-6 border-b border-gray-100 dark:border-slate-700">
            <h3 className="font-semibold text-gray-800 dark:text-slate-100 mb-2 flex items-center gap-2">
              <StepBadge n={2} /> บันทึก Backup Codes
            </h3>
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-4">
              <p className="text-amber-700 dark:text-amber-400 text-sm flex items-start gap-2">
                <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5 flex-shrink-0" />
                <span>เก็บ Backup Codes เหล่านี้ไว้ในที่ปลอดภัย สามารถใช้แทน OTP ได้ (ใช้ครั้งเดียว)</span>
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {backupCodes.map((code, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-50 dark:bg-slate-700 rounded-lg p-2.5 group">
                  <code className="flex-1 text-sm font-mono text-gray-700 dark:text-slate-200">{code}</code>
                  <button
                    onClick={() => copyToClipboard(code)}
                    className="opacity-0 group-hover:opacity-100 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 transition-opacity"
                  >
                    <FontAwesomeIcon icon={faCopy} className="text-xs" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Step 3: Verify OTP */}
          <div className="p-6">
            <h3 className="font-semibold text-gray-800 dark:text-slate-100 mb-4 flex items-center gap-2">
              <StepBadge n={3} /> ยืนยันด้วย OTP
            </h3>
            <form onSubmit={handleEnable}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                  กรอกรหัส OTP จากแอป (6 หลัก)
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  maxLength={6}
                  placeholder="000000"
                  className={OTP_INPUT_CLASS}
                  autoFocus
                />
              </div>
              <div className="flex gap-3">
                <UiButton
                  variant="secondary"
                  onClick={() => { setSetupMode(false); setOtp(''); }}
                  className="flex-1 justify-center"
                >
                  ยกเลิก
                </UiButton>
                <UiButton
                  type="submit"
                  loading={enablingOtp}
                  icon={faCheck}
                  className="flex-1 justify-center"
                >
                  ยืนยันและเปิดใช้งาน
                </UiButton>
              </div>
            </form>
          </div>
        </div>

      ) : (
        /* ── Main View ─────────────────────────────────────────────────── */
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme overflow-hidden">

          {/* Status */}
          <div className="p-6 border-b border-gray-100 dark:border-slate-700">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0
                ${enabled
                  ? 'bg-green-100 dark:bg-green-900/30'
                  : 'bg-gray-100 dark:bg-slate-700'}`}
              >
                <FontAwesomeIcon
                  icon={enabled ? faLock : faUnlock}
                  className={`text-2xl ${enabled ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-slate-500'}`}
                />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-slate-100">สถานะ 2FA</h3>
                {loading ? (
                  <div className="h-4 w-24 bg-gray-100 dark:bg-slate-700 rounded animate-pulse mt-1" />
                ) : (
                  <p className={`font-medium flex items-center gap-2 mt-0.5
                    ${enabled ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-slate-400'}`}
                  >
                    {enabled
                      ? <><FontAwesomeIcon icon={faCheck} /> เปิดใช้งานแล้ว</>
                      : <><FontAwesomeIcon icon={faTimes} /> ปิดอยู่</>}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Info box */}
          <div className="p-6 border-b border-gray-100 dark:border-slate-700">
            {enabled ? (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
                <p className="text-green-700 dark:text-green-400 flex items-start gap-2 text-sm">
                  <FontAwesomeIcon icon={faCheck} className="mt-0.5 flex-shrink-0" />
                  <span>บัญชีของคุณได้รับการปกป้องด้วย 2FA แล้ว ทุกครั้งที่ login จะต้องกรอกรหัส OTP จากแอป Google Authenticator</span>
                </p>
              </div>
            ) : (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                <p className="text-amber-700 dark:text-amber-400 flex items-start gap-2 text-sm">
                  <FontAwesomeIcon icon={faKey} className="mt-0.5 flex-shrink-0" />
                  <span>แนะนำให้เปิดใช้งาน 2FA เพื่อเพิ่มความปลอดภัยให้กับบัญชีของคุณ</span>
                </p>
              </div>
            )}
          </div>

          {/* Action */}
          <div className="p-6 border-b border-gray-100 dark:border-slate-700">
            {enabled ? (
              <UiButton
                variant="danger"
                icon={faUnlock}
                onClick={() => setShowDisableModal(true)}
                disabled={loading}
                className="w-full justify-center py-3"
              >
                ปิดใช้งาน 2FA
              </UiButton>
            ) : (
              <UiButton
                icon={faShieldHalved}
                onClick={handleSetup}
                disabled={loading}
                className="w-full justify-center py-3"
              >
                เริ่มตั้งค่า 2FA
              </UiButton>
            )}
          </div>

          {/* Info list */}
          <div className="p-6 bg-gray-50 dark:bg-slate-700/50">
            <h3 className="font-semibold text-gray-800 dark:text-slate-100 mb-3 flex items-center gap-2">
              <FontAwesomeIcon icon={faCircleInfo} style={{ color: 'var(--theme-primary)' }} />
              ข้อมูลเกี่ยวกับ 2FA
            </h3>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-slate-400">
              <li className="flex items-start gap-2">
                <FontAwesomeIcon icon={faShieldHalved} className="text-gray-400 dark:text-slate-500 mt-0.5 flex-shrink-0" />
                Two-Factor Authentication เพิ่มความปลอดภัยอีกชั้นหนึ่ง
              </li>
              <li className="flex items-start gap-2">
                <FontAwesomeIcon icon={faMobileAlt} className="text-gray-400 dark:text-slate-500 mt-0.5 flex-shrink-0" />
                ต้องติดตั้งแอป Google Authenticator บนมือถือ
              </li>
              <li className="flex items-start gap-2">
                <FontAwesomeIcon icon={faKey} className="text-gray-400 dark:text-slate-500 mt-0.5 flex-shrink-0" />
                ทุกครั้งที่ login จะต้องกรอกรหัส OTP 6 หลักจากแอป
              </li>
              <li className="flex items-start gap-2">
                <FontAwesomeIcon icon={faLock} className="text-gray-400 dark:text-slate-500 mt-0.5 flex-shrink-0" />
                Backup Codes สามารถใช้แทน OTP ได้ (ใช้ครั้งเดียว)
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* ── Disable Confirm Modal ───────────────────────────────────────── */}
      <UiModal
        open={showDisableModal}
        onClose={() => { setShowDisableModal(false); setDisableOtp(''); }}
        title="ปิดใช้งาน 2FA"
        icon={faUnlock}
        iconColor="text-red-500"
        size="sm"
      >
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-5">
          <p className="text-red-600 dark:text-red-400 text-sm flex items-start gap-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5 flex-shrink-0" />
            <span>การปิด 2FA จะลดความปลอดภัยของบัญชี กรุณายืนยันด้วยรหัส OTP จากแอป</span>
          </p>
        </div>
        <form onSubmit={handleDisable}>
          <label className="block text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-2">
            รหัส OTP (6 หลัก)
          </label>
          <input
            type="text"
            value={disableOtp}
            onChange={(e) => setDisableOtp(e.target.value.replace(/\D/g, ''))}
            maxLength={6}
            placeholder="000000"
            className={OTP_INPUT_CLASS}
            autoFocus
          />
          <div className="flex gap-3 mt-5">
            <UiButton
              variant="secondary"
              onClick={() => { setShowDisableModal(false); setDisableOtp(''); }}
              className="flex-1 justify-center"
            >
              ยกเลิก
            </UiButton>
            <UiButton
              type="submit"
              variant="danger"
              loading={disabling}
              icon={faTimes}
              className="flex-1 justify-center"
            >
              ยืนยันและปิดใช้งาน
            </UiButton>
          </div>
        </form>
      </UiModal>

    </div>
  );
};

export default Admin2FAPage;
